/**
 * Enum for react-query query keys
 * @readonly
 * @enum {string}
 */
export const QUERY_KEYS = {
  TEAM_MEMBER_IMG: 'teamMemberImage',
  TEAM_MEMBER_APPLICATION_ROLES: 'teamMemberApplicationRoles',
  PLANT_DATA: 'plantData',
  RESOURCE_DATA: 'resourceData',
  LOCATION_DATA: 'locationData',
  ACTION_DATA: 'actionData',
  QUEUE_DATA: 'queueData',
  REQUESTS_BY_LOCATION: 'requestsByLocation',
  REQUESTS_BY_RESOURCE: 'requestsByResource',
  COMPLETED_REQUESTS_BY_LOCATION: 'completedRequestsByLocation',
  COMPLETED_REQUESTS_BY_RESOURCE: 'completedRequestsByResource',
  RESOURCE_QUEUES: 'resourceQueues',
  ORGANIZATION_DATA: 'organizationData',
  SELECTION_LISTS: 'selectionLists',
  TRANSACTION_TYPES: 'transactionTypes',
  DISPOSITIONS: 'dispositions',
  CARRIERS: 'carriers',
  INVENTORY_ITEM: 'inventoryItem',
  SUBINVENTORY: 'subinventory',
  SUBINVENTORY_INFO: 'subinventoryInfo',
  LOCATORS: 'locators',
  PART_DESCRIPTION_DATA: 'partDescriptionData',
  QOH_BY_ORG_AND_ITEM: 'qohByOrgAndItem',
  DIE_AND_DESCRIPTION: 'dieAndDescription',
  LUMBER_ITEM_INFO_BY_SCHEDULE: 'lumberItemInfoBySchedule',
  LUMBER_LOCATION_BY_ITEM: 'lumberLocationByItem',
  PUT_DOWN_MILL_BY_ITEM: 'putDownMillByItem',
};

/**
 * Array of query keys to use for the global loading component
 * @type {string[]}
 */
export const USE_GLOBAL_LOADING = [QUERY_KEYS.TEAM_MEMBER_APPLICATION_ROLES];

/**
 * Fields to search in worklist requests
 * @type {string[]}
 */
export const SEARCH_FIELDS = [
  'action.description',
  'action.message',
  'action.location.description',
  'comment',
  'dynamicInput',
  'dynamicInputResult',
  'requestorId',
  'acceptUserId',
  'onHoldUserId',
  'completionUserId',
];

/**
 * @type {Record<string, Pella.ProductionRequestAPI.Request['status']>}
 */
export const STATUSES = {
  ACCEPTED: 'Accepted',
  AVAILABLE: 'Available',
  ON_HOLD: 'On Hold',
  COMPLETED: 'Completed',
  RECALLED: 'Recalled',
};

/**
 * Transaction types enum
 * @readonly
 * @enum {string}
 */
export const TRANSACTION_TYPES = {
  TRAILER_TO_DOCK: 'TRAILER_TO_DOCK',
  SUB_INVENTORY_TRANSFER: 'SUB_INVENTORY_TRANSFER',
  DIRECT_ORG_TRANSFER: 'DIRECT_ORG_TRANSFER',
  ISSUE_TO_LINE: 'ISSUE_TO_LINE',
};

/**
 * Selectable transaction types, in display order
 * @type {{id: string, description: string}[]}
 */
export const TRANSACTION_TYPE_OPTIONS = [
  { id: TRANSACTION_TYPES.TRAILER_TO_DOCK, description: 'Trailer to Dock' },
  { id: TRANSACTION_TYPES.SUB_INVENTORY_TRANSFER, description: 'Sub Inventory Transfer' },
  { id: TRANSACTION_TYPES.DIRECT_ORG_TRANSFER, description: 'Direct Org Transfer' },
  { id: TRANSACTION_TYPES.ISSUE_TO_LINE, description: 'Issue to Line' },
];

/**
 * Set of valid transaction types for validation
 * @type {Set<string>}
 */
export const TRANSACTION_TYPES_SET = new Set(Object.values(TRANSACTION_TYPES));

/**
 * Query types object for selection list queries
 * @type {{id: string, description: string}[]}
 */
export const QUERY_TYPES = [
  { id: 'PART_DESCRIPTION_LOOKUP', description: 'Part Description Lookup' },
  { id: 'PART_QOH_LOCATIONS_LOOKUP', description: 'Part QOH Locations Lookup' },
  { id: 'DIE_NUMBER_LOOKUP', description: 'Die Number Lookup' },
  { id: 'LUMBER_SCHEDULE_LOOKUP', description: 'Lumber Schedule Lookup' },
  { id: 'LUMBER_PART_LOOKUP', description: 'Lumber Part Lookup' },
  { id: 'P10_PUT_DOWN_MILL', description: 'P10 Put Down Mill' },
];
/**
 * Maximum number of characters accepted in a single request communication
 * @type {number}
 */
export const MAX_COMMUNICATION_LENGTH = 100;

export const HOLD_REASONS = {
  cartNotReady: 'Cart Not Ready',
  partNotReady: 'Part Not Ready',
  partOutage: 'Part Outage',
  routeBlockage: 'Route Blockage',
  qualityHold: 'Quality Hold',
  other: 'Other',
};

const defaultOutput = `
    id
    createdBy
    creationDate
    lastUpdatedBy
    lastUpdatedDate
`;

export const queueOutput = `
    description
    queueType
    active
    ${defaultOutput}
`;

const ACTION_FIELDS = `
    description
    message
    isDefault
    active
    sequence
    actionPrompt
    showComment
    showDynamicInput
    showQuantity
    showSelectionList
    selectionListId1
    selectionListId2
    actionQuery
    transactionId
    dockSelectionListId
    dispositionId
    completionId
    showOnHoldReason
    linkedActionId
    linkedAction {
        id
        description
        active
        location {
            id
            plant {
                id
            }
        }
    }
    ${defaultOutput}
`;

export const plantOutput = `
    active
    description
    organizationId
    wmsOrgCode
    ${defaultOutput}
`;

export const ACTION = `
    ${ACTION_FIELDS}
    queue {
        ${queueOutput}
        }
`;

export const ACTION_WITH_PLANT = `
    ${ACTION_FIELDS}
    queue {
        ${queueOutput}
        plant {
            ${plantOutput}
        }
    }
`;

export const COMMUNICATION = `
  communication
  userName
  timeStamp
  readBy
`;
export const NEW_COMMUNICATION_MESSAGE = 'A new communication has been received';
